import { useEffect, useMemo, useRef, useState } from "react";
import { useDataContext } from "../context/DataContext";
import { useViewPreferences } from "../context/ViewPreferencesContext";
import "./Styles/TravelPage.css";

interface Flight {
  flightId: string;
  flightNumber: string;
  carrier: string;
  departAt: Date | null;
  arriveBy: Date | null;
  departAirport: string;
  departTerminal: string;
  arriveAirport: string;
  arriveTerminal: string;
  flightNotes: string | null;
  bookingNotes: string | null;
  carrierNotes: string | null;
}

interface FlightBooking {
  bookingId: string;
  flight: Flight;
}

interface SupervisedPerson {
  id: string;
  name: string;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const pickString = (record: UnknownRecord, keys: string[]): string | null => {
  for (const key of keys) {
    const value = record[key];
    if (value === null || value === undefined) continue;
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
  }
  return null;
};

const extractRows = (data: unknown): UnknownRecord[] => {
  if (Array.isArray(data)) {
    return data.filter(isRecord);
  }
  if (isRecord(data)) {
    const possibleArray = data["value"];
    if (Array.isArray(possibleArray)) {
      return possibleArray.filter(isRecord);
    }
    const dataField = data["data"];
    if (Array.isArray(dataField)) {
      return dataField.filter(isRecord);
    }
    if (isRecord(dataField)) {
      const nestedValue = dataField["value"];
      if (Array.isArray(nestedValue)) {
        return nestedValue.filter(isRecord);
      }
    }
    return [data];
  }
  return [];
};

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date;
}

function formatTime(date: Date | null): string {
  if (!date) return "—";
  return date.toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC"
  });
}

function formatDate(date: Date | null): string {
  if (!date) return "—";
  return date.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC"
  });
}

function getFlightStatus(departAt: Date | null): "upcoming" | "today" | "past" {
  if (!departAt) return "upcoming";
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const flightDate = new Date(departAt.getFullYear(), departAt.getMonth(), departAt.getDate());
  
  if (flightDate.getTime() < today.getTime()) return "past";
  if (flightDate.getTime() === today.getTime()) return "today";
  return "upcoming";
}

function getDaysUntil(departAt: Date | null): number | null {
  if (!departAt) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const flightDate = new Date(departAt.getFullYear(), departAt.getMonth(), departAt.getDate());
  const diffTime = flightDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function getAirportCode(airport: string): string {
  if (airport.toLowerCase().includes("perth")) return "PER";
  if (airport.toLowerCase().includes("sydney")) return "SYD";
  if (airport.toLowerCase().includes("melbourne")) return "MEL";
  if (airport.toLowerCase().includes("brisbane")) return "BNE";
  if (airport.toLowerCase().includes("adelaide")) return "ADL";
  if (airport.toLowerCase().includes("darwin")) return "DRW";
  if (airport.toLowerCase().includes("hobart")) return "HBA";
  if (airport.toLowerCase().includes("cairns")) return "CNS";
  if (airport.toLowerCase().includes("gold coast")) return "OOL";
  if (!airport || airport.trim() === "") return "TBD";
  return airport.substring(0, 3).toUpperCase();
}

function getCarrierLogo(carrier: string): string {
  const c = carrier.toLowerCase();
  if (c.includes("qantas")) return "🦘";
  if (c.includes("virgin")) return "✈️";
  if (c.includes("jetstar")) return "⭐";
  if (c.includes("rex")) return "🛩️";
  return "✈️";
}

function parseFlightBookings(data: unknown): FlightBooking[] {
  const rows = extractRows(data);
  
  return rows.map((booking) => {
    const flightData = booking["Flight"];
    const flight: Flight = {
      flightId: "",
      flightNumber: "—",
      carrier: "—",
      departAt: null,
      arriveBy: null,
      departAirport: "",
      departTerminal: "",
      arriveAirport: "",
      arriveTerminal: "",
      flightNotes: null,
      bookingNotes: null,
      carrierNotes: null
    };
    
    if (isRecord(flightData)) {
      flight.flightId = pickString(flightData, ["FlightId"]) ?? "";
      flight.flightNumber = pickString(flightData, ["FlightNumber"]) ?? "—";
      flight.carrier = pickString(flightData, ["Carrier"]) ?? "—";
      flight.departAt = parseDate(pickString(flightData, ["DepartAt"]));
      flight.arriveBy = parseDate(pickString(flightData, ["ArriveBy"]));
      flight.departAirport = pickString(flightData, ["DepartAirport"]) ?? "";
      flight.departTerminal = pickString(flightData, ["DepartTerminal"]) ?? "";
      flight.arriveAirport = pickString(flightData, ["ArriveAirport"]) ?? "";
      flight.arriveTerminal = pickString(flightData, ["ArriveTerminal"]) ?? "";
      flight.flightNotes = pickString(flightData, ["FlightNotes"]);
      flight.bookingNotes = pickString(flightData, ["BookingNotes"]);
      flight.carrierNotes = pickString(flightData, ["CarrierNotes"]);
    }
    
    return {
      bookingId: pickString(booking, ["FlightBookingId"]) ?? `booking-${Math.random()}`,
      flight
    };
  });
}

function parseSupervisedPeople(data: unknown, rootPersonnelId: string): SupervisedPerson[] {
  const rows = extractRows(data);
  const normalizedRootId = rootPersonnelId.toLowerCase();

  const people: SupervisedPerson[] = rows.map((person, idx) => {
    const given = (pickString(person, ["GivenNames", "GivenName"]) || "").trim();
    const surname = (pickString(person, ["Surname", "LastName"]) || "").trim();
    const name = [given, surname].filter(Boolean).join(" ").trim() || `Person ${idx + 1}`;
    const id = pickString(person, ["Id", "PersonnelId"]) ?? `person-${idx}`;
    return { id, name };
  });

  // Sort: root person first, then alphabetically
  people.sort((a, b) => {
    const aIsRoot = a.id.toLowerCase() === normalizedRootId;
    const bIsRoot = b.id.toLowerCase() === normalizedRootId;
    if (aIsRoot && !bIsRoot) return -1;
    if (!aIsRoot && bIsRoot) return 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });

  return people;
}

// Helper to extract root person from root data
function extractRootPerson(data: unknown): UnknownRecord | null {
  if (!data) return null;
  if (isRecord(data)) {
    const dataValue = data["data"];
    if (isRecord(dataValue)) {
      if (Array.isArray(dataValue["value"])) {
        const arr = dataValue["value"].filter(isRecord);
        return arr[0] ?? null;
      }
      if (dataValue["PersonnelId"] || dataValue["Id"] || dataValue["GivenNames"]) {
        return dataValue;
      }
    }
    if (data["PersonnelId"] || data["Id"] || data["GivenNames"]) {
      return data;
    }
  }
  return null;
}

function FlightCard({ booking }: { booking: FlightBooking }) {
  const { flight } = booking;
  const status = getFlightStatus(flight.departAt);
  const daysUntil = getDaysUntil(flight.departAt);
  const departCode = getAirportCode(flight.departAirport);
  const arriveCode = getAirportCode(flight.arriveAirport);
  const carrierLogo = getCarrierLogo(flight.carrier);
  
  const hasNotes = flight.bookingNotes && flight.bookingNotes !== "-" && flight.bookingNotes.trim() !== "";
  
  return (
    <div className={`flight-card flight-card--${status}`}>
      <div className="flight-card__header">
        <div className="flight-card__carrier">
          <span className="flight-card__carrier-logo">{carrierLogo}</span>
          <span className="flight-card__carrier-name">{flight.carrier}</span>
        </div>
        <div className="flight-card__flight-number">{flight.flightNumber}</div>
      </div>
      
      <div className="flight-card__route">
        <div className="flight-card__airport flight-card__airport--depart">
          <div className="flight-card__airport-code">{departCode}</div>
          <div className="flight-card__airport-name">{flight.departAirport || "Departure"}</div>
          {flight.departTerminal && (
            <div className="flight-card__terminal">Terminal {flight.departTerminal}</div>
          )}
        </div>
        
        <div className="flight-card__flight-path">
          <div className="flight-card__path-line">
            <svg viewBox="0 0 100 20" className="flight-card__plane-svg">
              <line x1="0" y1="10" x2="42" y2="10" stroke="currentColor" strokeWidth="2" strokeDasharray="4,2" />
              <polygon points="58,10 48,5 48,15" fill="currentColor" />
              <line x1="58" y1="10" x2="100" y2="10" stroke="currentColor" strokeWidth="2" strokeDasharray="4,2" />
            </svg>
          </div>
        </div>
        
        <div className="flight-card__airport flight-card__airport--arrive">
          <div className="flight-card__airport-code">{arriveCode}</div>
          <div className="flight-card__airport-name">{flight.arriveAirport || "Arrival"}</div>
          {flight.arriveTerminal && (
            <div className="flight-card__terminal">Terminal {flight.arriveTerminal}</div>
          )}
        </div>
      </div>
      
      <div className="flight-card__times">
        <div className="flight-card__time flight-card__time--depart">
          <div className="flight-card__time-label">Departs</div>
          <div className="flight-card__time-value">{formatTime(flight.departAt)}</div>
        </div>
        <div className="flight-card__date">
          <div className="flight-card__date-value">{formatDate(flight.departAt)}</div>
          {status === "upcoming" && daysUntil !== null && daysUntil > 0 && (
            <div className="flight-card__countdown">
              {daysUntil === 1 ? "Tomorrow" : `In ${daysUntil} days`}
            </div>
          )}
          {status === "today" && (
            <div className="flight-card__countdown flight-card__countdown--today">Today</div>
          )}
          {status === "past" && (
            <div className="flight-card__countdown flight-card__countdown--past">Completed</div>
          )}
        </div>
        <div className="flight-card__time flight-card__time--arrive">
          <div className="flight-card__time-label">Arrives</div>
          <div className="flight-card__time-value">{formatTime(flight.arriveBy)}</div>
        </div>
      </div>
      
      {hasNotes && (
        <div className="flight-card__notes">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
          <span>{flight.bookingNotes}</span>
        </div>
      )}
      
      <div className="flight-card__barcode">
        <div className="flight-card__barcode-lines"></div>
      </div>
    </div>
  );
}

export default function TravelPage(): JSX.Element {
  const { supervisedData, rootAppData, currentPersonnelId, flightBookingsData, isSupervisor } = useDataContext();
  const { travelPrefs, setTravelSelectedPerson } = useViewPreferences();
  const { selectedPersonId } = travelPrefs;
  const rootPersonnelId = currentPersonnelId;
  
  // When not in supervisor mode, use the settings-selected person directly
  const effectivePersonId = isSupervisor ? selectedPersonId : rootPersonnelId;
  
  // Toggle for showing historical (past) travel - default OFF
  const [showHistorical, setShowHistorical] = useState(false);

  // Parse supervised people for dropdown
  const supervisedPeople = useMemo(() => {
    if (!supervisedData) return [];
    
    // Include root person if not already in supervised list
    const people = parseSupervisedPeople(supervisedData, rootPersonnelId);
    const rootPerson = extractRootPerson(rootAppData);
    
    if (rootPerson && rootPersonnelId) {
      const rootExists = people.some(p => p.id.toLowerCase() === rootPersonnelId.toLowerCase());
      if (!rootExists) {
        const given = (pickString(rootPerson, ["GivenNames", "GivenName"]) || "").trim();
        const surname = (pickString(rootPerson, ["Surname", "LastName"]) || "").trim();
        const name = [given, surname].filter(Boolean).join(" ").trim() || "You";
        people.unshift({ id: rootPersonnelId, name });
      }
    }
    
    return people;
  }, [supervisedData, rootAppData, rootPersonnelId]);

  // Searchable dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Build a map of personnelId -> flight bookings from context data
  // This includes both the root user's bookings and all supervised personnel bookings
  const flightBookingsMap = useMemo(() => {
    const map = new Map<string, UnknownRecord[]>();
    
    // Add root user's flight bookings from flightBookingsData
    if (rootPersonnelId && flightBookingsData) {
      const rootBookings = extractRows(flightBookingsData);
      map.set(rootPersonnelId.toLowerCase(), rootBookings);
    }
    
    // Add supervised personnel flight bookings from supervisedData
    if (supervisedData) {
      const supervisedRows = extractRows(supervisedData);
      supervisedRows.forEach((person) => {
        const personId = pickString(person, ["PersonnelId", "Id"]);
        if (!personId) return;
        
        // Look for TravelBookings or Flights array on the person object
        const travelBookings = person["TravelBookings"] ?? person["Flights"] ?? person["travelBookings"] ?? person["flights"];
        if (Array.isArray(travelBookings)) {
          map.set(personId.toLowerCase(), travelBookings.filter(isRecord));
        }
      });
    }
    
    return map;
  }, [rootPersonnelId, flightBookingsData, supervisedData]);

  // Get bookings for the currently selected person from the map (no API call needed)
  const bookingsData = useMemo(() => {
    if (!effectivePersonId) return null;
    const bookings = flightBookingsMap.get(effectivePersonId.toLowerCase());
    if (!bookings) return null;
    // Return in format expected by parseFlightBookings
    return { data: { value: bookings } };
  }, [effectivePersonId, flightBookingsMap]);

  // Set default selected person when data loads (first person in list)
  useEffect(() => {
    if (supervisedPeople.length > 0) {
      const currentExists = selectedPersonId && supervisedPeople.some(p => p.id === selectedPersonId);
      if (!currentExists) {
        setTravelSelectedPerson(supervisedPeople[0].id);
      }
    }
  }, [supervisedPeople, selectedPersonId, setTravelSelectedPerson]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setDropdownSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (dropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [dropdownOpen]);

  const selectedPerson = useMemo(() => {
    return supervisedPeople.find(p => p.id === effectivePersonId) ?? null;
  }, [supervisedPeople, effectivePersonId]);

  const filteredDropdownPeople = useMemo(() => {
    if (!dropdownSearch.trim()) return supervisedPeople;
    const term = dropdownSearch.toLowerCase();
    return supervisedPeople.filter(p => p.name.toLowerCase().includes(term));
  }, [supervisedPeople, dropdownSearch]);

  const bookings = useMemo(() => {
    if (!bookingsData) return [];
    return parseFlightBookings(bookingsData);
  }, [bookingsData]);
  
  // Separate future and past bookings, sort each by date (earliest first)
  // Then combine: future first, then past (if showHistorical is true)
  const sortedBookings = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Separate into future (today or later) and past
    const futureBookings: FlightBooking[] = [];
    const pastBookings: FlightBooking[] = [];
    
    bookings.forEach(booking => {
      const departAt = booking.flight.departAt;
      if (!departAt) {
        // No date - treat as future
        futureBookings.push(booking);
      } else {
        const flightDate = new Date(departAt.getFullYear(), departAt.getMonth(), departAt.getDate());
        if (flightDate.getTime() >= today.getTime()) {
          futureBookings.push(booking);
        } else {
          pastBookings.push(booking);
        }
      }
    });
    
    // Sort each group by date (earliest first)
    const sortByDate = (a: FlightBooking, b: FlightBooking) => {
      const aDate = a.flight.departAt?.getTime() ?? 0;
      const bDate = b.flight.departAt?.getTime() ?? 0;
      return aDate - bDate;
    };
    
    futureBookings.sort(sortByDate);
    pastBookings.sort(sortByDate);
    
    // Combine: future first, then past (only if showHistorical is true)
    if (showHistorical) {
      return [...futureBookings, ...pastBookings];
    }
    return futureBookings;
  }, [bookings, showHistorical]);
  
  // Count of past bookings for the toggle label
  const pastBookingsCount = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return bookings.filter(booking => {
      const departAt = booking.flight.departAt;
      if (!departAt) return false;
      const flightDate = new Date(departAt.getFullYear(), departAt.getMonth(), departAt.getDate());
      return flightDate.getTime() < today.getTime();
    }).length;
  }, [bookings]);
  
  // Group bookings by month
  const groupedBookings = useMemo(() => {
    const groups: Map<string, FlightBooking[]> = new Map();
    
    sortedBookings.forEach(booking => {
      const date = booking.flight.departAt;
      const key = date 
        ? date.toLocaleDateString("en-AU", { month: "long", year: "numeric", timeZone: "UTC" })
        : "Unscheduled";
      
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(booking);
    });
    
    return groups;
  }, [sortedBookings]);

  const handleSelectPerson = (id: string) => {
    setTravelSelectedPerson(id);
    setDropdownOpen(false);
    setDropdownSearch("");
  };
  
  return (
    <div className="travel-page">
      <div className="travel-header">
        <div className="travel-header__controls">
          {isSupervisor && (
          <div className="travel-header__person-select" ref={dropdownRef}>
            <button
              type="button"
              className="travel-person-dropdown"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
            >
              <span className="travel-person-dropdown__text">
                {selectedPerson?.name ?? "Select person..."}
              </span>
              <span className="travel-person-dropdown__arrow">
                {dropdownOpen ? "▲" : "▼"}
              </span>
            </button>
            {dropdownOpen && (
              <div className="travel-dropdown-menu">
                <div className="travel-dropdown-search">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search..."
                    value={dropdownSearch}
                    onChange={(e) => setDropdownSearch(e.target.value)}
                    className="travel-dropdown-search__input"
                  />
                </div>
                <div className="travel-dropdown-list">
                  {filteredDropdownPeople.length === 0 ? (
                    <div className="travel-dropdown-empty">No matches found</div>
                  ) : (
                    filteredDropdownPeople.map(person => (
                      <button
                        key={person.id}
                        type="button"
                        className={`travel-dropdown-item${person.id === effectivePersonId ? " travel-dropdown-item--selected" : ""}`}
                        onClick={() => handleSelectPerson(person.id)}
                      >
                        {person.name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          )}
            {pastBookingsCount > 0 && (
              <button
                type="button"
                className={`travel-history-toggle${showHistorical ? " travel-history-toggle--active" : ""}`}
                onClick={() => setShowHistorical(!showHistorical)}
                aria-pressed={showHistorical}
                aria-label={`${showHistorical ? "Hide" : "Show"} history (${pastBookingsCount})`}
                title={`${showHistorical ? "Hide" : "Show"} history (${pastBookingsCount})`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </button>
            )}
        </div>
      </div>
      
      <div className="travel-content">
        {sortedBookings.length === 0 ? (
          <div className="travel-empty">
            <div className="travel-empty__icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
              </svg>
            </div>
            <p className="travel-empty__text">No flight bookings found</p>
          </div>
        ) : (
          Array.from(groupedBookings.entries()).map(([month, monthBookings]) => (
            <div key={month} className="travel-month-group">
              <div className="travel-month-group__header">{month}</div>
              <div className="travel-month-group__cards">
                {monthBookings.map(booking => (
                  <FlightCard key={booking.bookingId} booking={booking} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
